import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { LuArrowLeft, LuInfo, LuFileText, LuLoader, LuSave } from "react-icons/lu";
import { FaExclamationCircle } from "react-icons/fa";
import api from "../../libs/axios";
import { useAuth } from "../../provider/auth.provider";
import { SelectWithSearch, SelectOption } from "../../utils/select-with-search";
import { DESIGN_ACTIONS, DESIGN_PRIORITIES, DESIGN_REPORT_TYPES, DESIGN_RESOURCE } from "./design_constants";

// Every Designing project picker offers the ACTIVE projects the logged-in user
// is assigned to (user_projects) — the scope the server enforces on create,
// edit and both lists. Role level 1 is the one exemption: a super admin may use
// any project, so `assigned_only` is left out for them. This is the only role
// check in the module; everything else is a DesignSite permission.
export const designProjectParams = (roleLevel?: number): Record<string, string | boolean> =>
  roleLevel === 1 ? { status: "ACTIVE" } : { status: "ACTIVE", assigned_only: true };

const inputBase =
  "w-full px-3 py-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm";
const selectClass =
  "w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm disabled:bg-gray-100 disabled:cursor-not-allowed";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";
const hintClass = "mt-1 text-xs text-gray-500";
const cardClass = "bg-gray-50 border border-gray-200 rounded p-4 sm:p-6";
const cardTitleClass = "text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2";

const FieldError: React.FC<{ error?: string }> = ({ error }) =>
  error ? (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <FaExclamationCircle className="w-3 h-3" />
      {error}
    </p>
  ) : null;

// Module-level input so identity is stable across renders (prevents focus loss).
interface FieldProps {
  label: string;
  value: any;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  placeholder?: string;
  textarea?: boolean;
  maxLength?: number;
}
const Field: React.FC<FieldProps> = ({ label, value, onChange, type = "text", required, error, placeholder, textarea, maxLength }) => (
  <div>
    <label className={labelClass}>
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        className={`${inputBase} resize-none ${error ? "border-red-300 bg-red-50" : "border-gray-300"}`}
      />
    ) : (
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`${inputBase} ${error ? "border-red-300 bg-red-50" : "border-gray-300"}`}
      />
    )}
    <FieldError error={error} />
  </div>
);

interface DesignSiteFormProps {
  mode: "create" | "edit";
  siteId?: string | number;
}

const DesignSiteForm: React.FC<DesignSiteFormProps> = ({ mode, siteId }) => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  const [formData, setFormData] = useState<any>({
    site_code: "",
    project_id: 0,
    reference_id: "",
    site_name: "",
    activity_id: 0,
    client_id: 0,
    report_type: "new",
    // "yes" | "no" | "" — a New report is always billable; a Revision has to say.
    is_billable: "yes",
    report_instruction1: "",
    report_instruction2: "",
    priority: "medium",
    required_date: "",
    po_number: "",
    po_date: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  // Edit only: the server refuses a project change once a report maker is
  // assigned, and refuses any edit of a cancelled site.
  const [projectLocked, setProjectLocked] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedClient, setSelectedClient] = useState<SelectOption | null>(null);

  // Whichever list the user is allowed to open.
  const listPath = hasPermission(DESIGN_RESOURCE, DESIGN_ACTIONS.VIEW_ALL) ? "/designing/sites" : "/designing/my-sites";

  const update = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: "" }));
  };

  const changeReportType = (value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      report_type: value,
      // New → always billable. Revision → a deliberate choice, no default.
      is_billable: value === "new" ? "yes" : prev.report_type === "revision" ? prev.is_billable : "",
    }));
    setErrors((e) => ({ ...e, report_type: "", is_billable: "" }));
  };

  useEffect(() => {
    if (mode !== "edit" || !siteId) return;
    (async () => {
      try {
        const res = await api.get(`/designing/sites/${siteId}`);
        const s = res.data?.data;
        if (s) {
          setFormData({
            site_code: s.site_code || "",
            project_id: s.project?.id || s.project_id || 0,
            reference_id: s.reference_id || "",
            site_name: s.site_name || "",
            activity_id: s.activity?.id || s.activity_id || 0,
            client_id: s.client?.id || s.client_id || 0,
            report_type: s.report_type === "revision" ? "revision" : "new",
            is_billable: s.report_type === "revision" ? (s.is_billable ? "yes" : "no") : "yes",
            report_instruction1: s.report_instruction1 || "",
            report_instruction2: s.report_instruction2 || "",
            priority: s.priority || "medium",
            required_date: s.required_date ? String(s.required_date).slice(0, 10) : "",
            po_number: s.po_number || "",
            po_date: s.po_date ? String(s.po_date).slice(0, 10) : "",
          });
          if (s.project) setSelectedProject({ id: s.project.id, label: s.project.project_name, value: s.project.id });
          if (s.activity) setSelectedActivity({ id: s.activity.id, label: s.activity.activity_name, value: s.activity.id });
          if (s.client) setSelectedClient({ id: s.client.id, label: s.client.client_full_name || s.client.client_short_name, value: s.client.id });
          setProjectLocked(!!(s.report_maker?.id || s.report_maker_id));
          setCancelled(s.status === "cancelled");
        }
      } catch {
        toast.error("Failed to load site");
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, siteId]);

  const getErrors = (data = formData): Record<string, string> => {
    const e: Record<string, string> = {};
    const siteCode = String(data.site_code || "").trim();
    if (!siteCode) e.site_code = "Site ID is required";
    else if (siteCode.length > 50) e.site_code = "Site ID must be at most 50 characters";
    if (!data.project_id) e.project_id = "Project is required";
    if (!data.report_type) e.report_type = "Report type is required";
    if (data.report_type === "revision" && !data.is_billable) e.is_billable = "Say whether this revision is billable";
    return e;
  };

  const handleSubmit = async () => {
    const e = getErrors();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const textOrNull = (v: any) => String(v || "").trim() || null;
      const payload = {
        site_code: String(formData.site_code).trim(),
        project_id: formData.project_id,
        reference_id: textOrNull(formData.reference_id),
        site_name: textOrNull(formData.site_name),
        activity_id: formData.activity_id || null,
        client_id: formData.client_id || null,
        report_type: formData.report_type,
        is_billable: formData.report_type === "new" ? true : formData.is_billable === "yes",
        report_instruction1: textOrNull(formData.report_instruction1),
        report_instruction2: textOrNull(formData.report_instruction2),
        priority: formData.priority || "medium",
        required_date: formData.required_date || null,
        po_number: textOrNull(formData.po_number),
        po_date: formData.po_date || null,
      };
      // The axios instance toasts the server's message for both outcomes.
      let id: string | number | undefined = siteId;
      if (mode === "create") {
        const res = await api.post("/designing/sites", payload);
        id = res.data?.data?.id;
      } else {
        await api.put(`/designing/sites/${siteId}`, payload);
      }
      navigate(id ? `/designing/sites/${id}` : listPath);
    } catch (err: any) {
      // 400 (validation) / 409 (Site ID already live in the project) carry `message`.
      setSubmitError(err?.response?.data?.message || "Failed to save site");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Loading...</div>;

  const isNewReport = formData.report_type === "new";

  return (
    <div className="mx-auto">
      <button onClick={() => navigate(listPath)} className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium mb-4">
        <LuArrowLeft className="w-4 h-4" /> Back to Sites
      </button>

      <div className="bg-white rounded border border-gray-200 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 truncate">
              {mode === "create" ? "Create Designing Site" : "Edit Designing Site"}
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
              {mode === "create" ? "Add a new site for report preparation" : "Update the site details"}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-6">
            {cancelled && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                This site is cancelled — a cancelled site cannot be edited.
              </div>
            )}

            <div className={cardClass}>
              <h3 className={cardTitleClass}><LuInfo className="w-5 h-5 text-blue-800" /> Site Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Site ID" value={formData.site_code} onChange={(v) => update("site_code", v)} required error={errors.site_code} placeholder="Site ID" maxLength={50} />
                <div>
                  <label className={labelClass}>Project <span className="text-red-500">*</span></label>
                  <SelectWithSearch
                    value={selectedProject}
                    onChange={(o) => { setSelectedProject(o); update("project_id", o?.id || 0); }}
                    apiEndpoint="/projects/list"
                    dataKey="projects"
                    labelKey="project_name"
                    placeholder="Search projects..."
                    minSearchLength={0}
                    extraParams={designProjectParams(user?.role?.level)}
                    disabled={projectLocked}
                  />
                  {projectLocked && <p className={hintClass}>The project cannot be changed once a report maker has been assigned</p>}
                  <FieldError error={errors.project_id} />
                </div>
                <Field label="Reference ID" value={formData.reference_id} onChange={(v) => update("reference_id", v)} maxLength={100} />
                <Field label="Site Name" value={formData.site_name} onChange={(v) => update("site_name", v)} maxLength={200} />
                <div>
                  <label className={labelClass}>Activity</label>
                  <SelectWithSearch
                    value={selectedActivity}
                    onChange={(o) => { setSelectedActivity(o); update("activity_id", o?.id || 0); }}
                    apiEndpoint="/activities/list"
                    dataKey="activities"
                    labelKey="activity_name"
                    placeholder="Search activities..."
                    minSearchLength={0}
                  />
                </div>
                <div>
                  <label className={labelClass}>Client</label>
                  <SelectWithSearch
                    value={selectedClient}
                    onChange={(o) => { setSelectedClient(o); update("client_id", o?.id || 0); }}
                    apiEndpoint="/clients/list"
                    dataKey="clients"
                    labelKey="client_full_name"
                    placeholder="Search clients..."
                    minSearchLength={0}
                  />
                  {/* Only create falls back to the project's client; an edit saves the field as it is. */}
                  {mode === "create" && <p className={hintClass}>Leave blank to use the project's client</p>}
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <h3 className={cardTitleClass}><LuFileText className="w-5 h-5 text-blue-800" /> Report Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Report Type <span className="text-red-500">*</span></label>
                  <select value={formData.report_type} onChange={(e) => changeReportType(e.target.value)} className={selectClass}>
                    {DESIGN_REPORT_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <FieldError error={errors.report_type} />
                </div>
                <div>
                  <label className={labelClass}>
                    Billable
                    {!isNewReport && <span className="text-red-500"> *</span>}
                  </label>
                  <select
                    value={isNewReport ? "yes" : formData.is_billable}
                    onChange={(e) => update("is_billable", e.target.value)}
                    disabled={isNewReport}
                    className={`${selectClass} ${errors.is_billable ? "border-red-300 bg-red-50" : ""}`}
                  >
                    {!isNewReport && <option value="">Select...</option>}
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {isNewReport && <p className={hintClass}>A new report is always billable</p>}
                  <FieldError error={errors.is_billable} />
                </div>
                <div className="md:col-span-2">
                  <Field label="Report Instruction 1" value={formData.report_instruction1} onChange={(v) => update("report_instruction1", v)} textarea />
                </div>
                <div className="md:col-span-2">
                  <Field label="Report Instruction 2" value={formData.report_instruction2} onChange={(v) => update("report_instruction2", v)} textarea />
                </div>
                <div>
                  <label className={labelClass}>Priority</label>
                  <select value={formData.priority} onChange={(e) => update("priority", e.target.value)} className={selectClass}>
                    {DESIGN_PRIORITIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <Field label="Required Date" value={formData.required_date} onChange={(v) => update("required_date", v)} type="date" />
                <Field label="PO Number" value={formData.po_number} onChange={(v) => update("po_number", v)} maxLength={100} />
                <Field label="PO Date" value={formData.po_date} onChange={(v) => update("po_date", v)} type="date" />
              </div>
            </div>

            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded flex items-start gap-2">
                <FaExclamationCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-white p-4 sm:p-6 flex-shrink-0">
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(listPath)}
              disabled={submitting}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || cancelled}
              className="flex items-center gap-2 px-6 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors justify-center"
            >
              {submitting ? (
                <><LuLoader className="w-4 h-4 animate-spin" /> {mode === "create" ? "Creating..." : "Saving..."}</>
              ) : (
                <><LuSave className="w-4 h-4" /> {mode === "create" ? "Create Site" : "Save Changes"}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignSiteForm;
